import { Box, Portal } from "@chakra-ui/react"
import { useObserveSizeContext } from "@pathwright/ui/src/components/observers/ObserveSizeContext"
import React from "react"
import { usePrevious } from "react-use"
import AugmentationToolbar, {
  AugmentationToolbarProps
} from "./AugmentationToolbar"
import { usePanelSize } from "./useAugmentationPanel"

type ToolbarPosition = "bottom" | "side"
type AugmentationPositionedToolbarProps = AugmentationToolbarProps & {
  allowOverlay?: boolean
  gutter?: number
  offset?: { left?: number } | { right?: number }
  components?: {
    bottom?: typeof ToolbarBottom
    side?: typeof ToolbarSide
  }
}

// Rendering the toolbar based on screen size.
// - "full": render at bottom of screen
// - sm+: render to the right side of screen
export function useToolbarPosition<T>(
  breakpointOptions: Record<ToolbarPosition, T>,
  allowOverlay?: boolean
): T {
  const panelSize = usePanelSize({ allowOverlay })
  const position = panelSize === "full" ? "bottom" : "side"
  return breakpointOptions[position]
}

// Eh, a lot of work just to get the toolbar positioned sticky
// next to top of registrations list.
const ToolbarSide = React.forwardRef<
  HTMLDivElement,
  AugmentationPositionedToolbarProps
>(function ToolbarSide(props, ref) {
  let { gutter, offset, yieldsToPanel, ...augmentationToolbarProps } = props
  gutter ??= 0
  offset ??= { right: 0 }
  let [alignToolbar, offsetValue] = Object.entries(offset)[0]
  offsetValue ??= 0

  const { getRectValue } = useObserveSizeContext()
  const alertHeight = getRectValue("alert.height")

  return (
    // NOTE: we need to accept a left/right offset to position the toolbar.
    <Box
      className="ToolbarSide"
      pos="absolute"
      top={0}
      {...{ [alignToolbar]: `${offsetValue}px` }}
      bottom={0}
      visibility={yieldsToPanel ? "hidden" : "visible"}
      pointerEvents={yieldsToPanel ? "none" : "auto"}
      aria-hidden={yieldsToPanel || undefined}
    >
      <Box zIndex={10001} pos="sticky" top={`${gutter + alertHeight}px`}>
        <AugmentationToolbar ref={ref} {...augmentationToolbarProps} />
      </Box>
    </Box>
  )
})

const ToolbarBottom = React.forwardRef<
  HTMLDivElement,
  AugmentationPositionedToolbarProps
>(function ToolbarBottom(props, ref) {
  let { gutter, yieldsToPanel, ...augmentationToolbarProps } = props
  gutter ??= 0

  return (
    <Portal>
      <Box
        className="ToolbarBottom"
        pos="fixed"
        bottom={`${gutter}px`}
        left={`${gutter}px`}
        // Intercom’s default launcher sits at bottom-right (20px + 48px).
        // Message cards still use that corner; keep the island + primary
        // action out of it.
        right="80px"
        zIndex="popover"
        maxW={`calc(100vw - ${gutter}px - 80px)`}
        // Room inside the clip for circular primary / island shadows.
        // overflow-x:auto forces overflow-y to auto as well, which would
        // otherwise slice box-shadows into a rectangle.
        px="12px"
        py="12px"
        overflowX="auto"
        overflowY="hidden"
        visibility={yieldsToPanel ? "hidden" : "visible"}
        pointerEvents={yieldsToPanel ? "none" : "auto"}
        aria-hidden={yieldsToPanel || undefined}
      >
        <AugmentationToolbar
          ref={ref}
          direction="row"
          {...augmentationToolbarProps}
        />
      </Box>
    </Portal>
  )
})

const AugmentationPositionedToolbar = React.forwardRef<
  HTMLDivElement,
  AugmentationPositionedToolbarProps
>(function AugmentationPositionedToolbar(props, ref) {
  const { allowOverlay, components, ...passProps } = props
  const ToolbarComponent = useToolbarPosition(
    {
      bottom: components?.bottom ?? ToolbarBottom,
      side: components?.side ?? ToolbarSide
    },
    allowOverlay
  )

  // useBreakpointValue renders the base initially on the first render,
  // no matter what the real breakboint is, so ignore the first output.
  const shouldRender = !!usePrevious(ToolbarComponent)

  return shouldRender ? (
    <ToolbarComponent ref={ref} {...passProps} alwaysVisible />
  ) : null
})

export default AugmentationPositionedToolbar
